import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "@env";

import { CarWorkshopDetailsDTO } from "@shared/models/car-workshop-details-dto";
import { CarWorkshopDTO } from "@shared/models/car-workshop-dto";
import { CarWorkshopContactsDTO } from "@shared/models/car-workshop-contacts-dto";
import { SearchFormDTO } from "src/app/modules/private/modules/car-workshop/components/models/search-form-dto";
import { SaveCarWorkshopDTO } from "@shared/models/save-car-workshop-dto";
import { SaveCarWorkshopContactDTO } from "@shared/models/save-car-workshop-contact-dto";
import { ClientSimplifiedListDTO } from "@shared/models/client-details-dto";
import { EmailDTO } from "@shared/models/email-dto";
import {
  NearbyCarWorkshopDTO,
  NearbyCarWorkshopFilterDTO,
  NearbyHistoryCarWorkshopDTO
} from "@shared/models/nearby-history-car-workshop-dto";
import { ServicesProvidedDTO } from "@shared/models/services-provided-dto";
import { Pagination } from "@shared/models/pagination";
import { CarWorkshopBlockHistoryDTO } from "src/app/shared/models/car-workshop-block-history-dto";
import {
  DISABLED_LOADING_INTERCEPTOR,
  DISABLED_NOTIFY_MESSAGE
} from "@core/constants";
import { CarWorkshopSimplifiedDTO } from "@shared/models/car-workshop-simplified-dto";
import { PageManager } from "@shared/models/page-manager";
import { PageFilterDto } from "@shared/page-filter-dto";
import { CarWorkshopFeeHistoryResponse } from "@shared/models/car-workshop-fee-history-dto";
import {
  CarWorkshopOriginDTO,
  CarWorkshopOriginResponseDTO
} from "@shared/models/car-workshop-origin-dto";

@Injectable({
  providedIn: "root"
})
export class CarWorkshopService {
  private carWorkshopUrl = "car-workshops/";
  private carWorkshopV2Url = "v2/car-workshops/";
  private contactUrl = "contacts/";
  private headers = new HttpHeaders({
    [DISABLED_NOTIFY_MESSAGE]: "true"
  });
  private carWorkshopOriginUrl = "car-workshop-origin/";

  constructor(private http: HttpClient) {}

  listAll(): Observable<CarWorkshopDTO[]> {
    return this.http.get<CarWorkshopDTO[]>(
      environment.apiUrl + this.carWorkshopV2Url
    ).pipe(
      catchError(() =>
        this.http.get<CarWorkshopDTO[]>(environment.apiUrl + this.carWorkshopUrl)
      )
    );
  }

  listPaged(
    pageManager: PageManager
  ): Observable<Pagination<CarWorkshopDTO[]>> {
    return this.http.get<Pagination<CarWorkshopDTO[]>>(
      environment.apiUrl +
        `${this.carWorkshopUrl}paged?page=${pageManager.pageIndex}&size=${pageManager.pageSize}`
    );
  }

  save(saveCarWorkshopDTO: SaveCarWorkshopDTO): Observable<any> {
    return this.http.post<any>(
      environment.apiUrl + this.carWorkshopUrl,
      saveCarWorkshopDTO
    );
  }

  delete(id: number): Observable<any> {
    return this.http.delete<any>(
      environment.apiUrl + `${this.carWorkshopUrl}${id}`
    );
  }

  findById(id: number): Observable<CarWorkshopDetailsDTO> {
    return this.http.get<CarWorkshopDetailsDTO>(
      environment.apiUrl + `${this.carWorkshopUrl}${id}`
    );
  }

  edit(id: number, saveCarWorkshopDTO: SaveCarWorkshopDTO): Observable<any> {
    return this.http.put<any>(
      environment.apiUrl + `${this.carWorkshopUrl}${id}`,
      saveCarWorkshopDTO
    );
  }

  listAllContact(
    id: number,
    listByOriginId: boolean = false
  ): Observable<CarWorkshopContactsDTO[]> {
    let query = "";
    if (listByOriginId) {
      query = `${this.carWorkshopOriginUrl}${id}/${this.contactUrl}`;
    } else {
      query = `${this.carWorkshopUrl}${id}/${this.contactUrl}`;
    }
    return this.http.get<CarWorkshopContactsDTO[]>(
      environment.apiUrl + `${query}`
    );
  }

  saveContact(
    id: number,
    saveCarWorkshopDTO: SaveCarWorkshopContactDTO
  ): Observable<any> {
    return this.http.post<any>(
      environment.apiUrl + `${this.carWorkshopUrl}${id}/${this.contactUrl}`,
      saveCarWorkshopDTO
    );
  }

  deleteContact(idCarWorkshop: number, idContact: number): Observable<any> {
    return this.http.delete<any>(
      environment.apiUrl +
        `${this.carWorkshopUrl}${idCarWorkshop}/${this.contactUrl}${idContact}`
    );
  }

  findByIdContact(id: number): Observable<CarWorkshopContactsDTO> {
    return this.http.get<CarWorkshopContactsDTO>(
      environment.apiUrl + `${this.carWorkshopUrl}${id}/${this.contactUrl}`
    );
  }

  editContact(
    idCarWorkshop: number,
    idContact: number,
    saveCarWorkshopDTO: SaveCarWorkshopContactDTO
  ): Observable<any> {
    return this.http.put<any>(
      environment.apiUrl +
        `${this.carWorkshopUrl}${idCarWorkshop}/${this.contactUrl}${idContact}`,
      saveCarWorkshopDTO
    );
  }

  buildQueryForFilters(searchFormDTO: SearchFormDTO): string {
    let query = "";

    if (searchFormDTO.tradingName) {
      query += `&tradingName=${searchFormDTO.tradingName}`;
    }
    if (searchFormDTO.clientId) {
      query += `&clientId=${searchFormDTO.clientId}`;
    }
    if (searchFormDTO.countryId) {
      query += `&countryId=${searchFormDTO.countryId}`;
    }
    if (searchFormDTO.stateId) {
      query += `&stateId=${searchFormDTO.stateId}`;
    }
    if (searchFormDTO.cityId) {
      query += `&cityId=${searchFormDTO.cityId}`;
    }
    if (searchFormDTO.filter) {
      query += `&filter=${searchFormDTO.filter}`;
    }
    if (searchFormDTO.corporateName) {
      query += `&corporateName=${searchFormDTO.corporateName}`;
    }
    if (searchFormDTO.taxIdentifier) {
      query += `&taxIdentifier=${searchFormDTO.taxIdentifier}`;
    }
    if (searchFormDTO.isBlocked != null && searchFormDTO.isBlocked != "") {
      query += `&isBlocked=${!JSON.parse(searchFormDTO.isBlocked)}`;
    }
    if (searchFormDTO.isFee === true || searchFormDTO.isFee === false) {
      query += `&isFee=${searchFormDTO.isFee}`;
    }

    return query;
  }

  findByFiltersAutoComplete(
    searchFormDTO: SearchFormDTO
  ): Observable<CarWorkshopSimplifiedDTO[]> {
    const query = this.buildQueryForFilters(searchFormDTO);

    return this.http.get<CarWorkshopSimplifiedDTO[]>(
      environment.apiUrl + `${this.carWorkshopUrl}autocomplete?${query}`
    );
  }

  findByFiltersAutoCompleteNegotiation(
    searchFormDTO: SearchFormDTO,
    negotiationId: number
  ): Observable<CarWorkshopSimplifiedDTO[]> {
    const query =
      this.buildQueryForFilters(searchFormDTO) +
      `&negotiationId=${negotiationId}`;
    return this.http.get<CarWorkshopSimplifiedDTO[]>(
      environment.apiUrl + `${this.carWorkshopUrl}autocomplete?${query}`
    );
  }

  findByFilters(
    searchFormDTO: SearchFormDTO,
    pageManager: PageManager,
    sort: string
  ): Observable<Pagination<CarWorkshopSimplifiedDTO[]>> {
    const query = this.buildQueryForFilters(searchFormDTO);

    return this.http.get<Pagination<CarWorkshopSimplifiedDTO[]>>(
      environment.apiUrl +
        `${this.carWorkshopUrl}filters?${query}&page=${pageManager.pageIndex}&size=${pageManager.pageSize}&${sort}`
    );
  }

  sendEmailByFilters(searchFormDTO: SearchFormDTO, emailDTO: EmailDTO) {
    const query = this.buildQueryForFilters(searchFormDTO);

    return this.http.post<any>(
      `${environment.apiUrl}${this.carWorkshopUrl}${this.contactUrl}email?${query}`,
      emailDTO
    );
  }

  findServicesProvidedByCarWorkshop(carWorkshopId: number) {
    return this.http.get<ServicesProvidedDTO[]>(
      `${environment.apiUrl}${this.carWorkshopUrl}${carWorkshopId}/services-provided`
    );
  }

  saveServices(carWorkshopId: number, servicesTypeIds: number[]) {
    return this.http.post<any>(
      `${environment.apiUrl}${this.carWorkshopUrl}${carWorkshopId}/services-provided`,
      servicesTypeIds
    );
  }

  updateModelCategories(carWorkshopId: number, modelCategories: number[]) {
    return this.http.put<any>(
      `${environment.apiUrl}${this.carWorkshopUrl}${carWorkshopId}/model-categories`,
      modelCategories
    );
  }

  listBlockedClients(id: number): Observable<ClientSimplifiedListDTO[]> {
    if (id != null) {
      return this.http.get<ClientSimplifiedListDTO[]>(
        `${environment.apiUrl}${this.carWorkshopUrl}${id}/blocked-client`
      );
    } else {
      return null;
    }
  }

  updateBlockedClients(id: number, payload: any): Observable<any> {
    if (id != null) {
      return this.http.post<any>(
        `${environment.apiUrl}${this.carWorkshopUrl}${id}/blocked-client`,
        payload
      );
    } else {
      return null;
    }
  }

  getPaginatedCarWorkshopsNearby(
    filter: NearbyCarWorkshopFilterDTO,
    body: NearbyCarWorkshopDTO
  ): Observable<Pagination<NearbyHistoryCarWorkshopDTO[]>> {
    let filteredParams = new HttpParams();
    for (const key in filter) {
      if (filter[key] || filter[key] == 0) {
        filteredParams = filteredParams.set(`${key}`, filter[key]);
      }
    }

    return this.http.post<Pagination<NearbyHistoryCarWorkshopDTO[]>>(
      `${environment.apiUrl}${this.carWorkshopUrl}nearby-paginated`,
      body,
      { params: filteredParams, headers: this.headers }
    );
  }

  getServicesProvidedWasFilled(carWorkshopId: number) {
    return this.http.get<boolean>(
      `${environment.apiUrl}${this.carWorkshopUrl}${carWorkshopId}/services-provided/filled`
    );
  }

  getServiceRequestAlertStatus(carWorkshopId: number) {
    const headers = new HttpHeaders({
      [DISABLED_LOADING_INTERCEPTOR]: "true"
    });

    return this.http.get<boolean>(
      `${environment.apiV2Url}maintenance-plans/there-pending-status/${carWorkshopId}`,
      { headers }
    );
  }

  getCategories(params: any) {
    return this.http.get<any>(`${environment.apiUrl}categories/filters`, {
      params: params
    });
  }

  getInvoicesPendencies(size = 24) {
    return this.http.get<Pagination<string[]>>(
      `${environment.apiUrl}${this.carWorkshopUrl}pendencies/invoices?size=${size}`
    );
  }

  getBudgetPendencies(size = 24) {
    return this.http.get<Pagination<string[]>>(
      `${environment.apiUrl}${this.carWorkshopUrl}pendencies/budget?size=${size}`
    );
  }

  getPreventiveProposals(size = 24) {
    return this.http.get<Pagination<string[]>>(
      `${environment.apiUrl}${this.carWorkshopUrl}pendencies/proposals?size=${size}`
    );
  }

  getBlockHistory = (
    carWorkshopId: number
  ): Observable<CarWorkshopBlockHistoryDTO[]> => {
    return this.http.get<CarWorkshopBlockHistoryDTO[]>(
      `${environment.apiUrl}${this.carWorkshopUrl}${carWorkshopId}/blocked-history`
    );
  };

  blockedCarWorkshop(id: number, reason: string) {
    return this.http.patch<any>(
      `${environment.apiUrl}${this.carWorkshopUrl}${id}/blocked`,
      reason
    );
  }

  getNetworkName(params: any) {
    return this.http.get<any>(`${environment.apiUrl}networks/filters`, {
      params: params
    });
  }

  findByMap(
    filter: PageFilterDto,
    body: NearbyCarWorkshopDTO
  ): Observable<Pagination<NearbyHistoryCarWorkshopDTO[]>> {
    let filteredParams = new HttpParams();

    for (const key in filter) {
      const value = filter[key];
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) {
          filteredParams = filteredParams.set(`${key}`, value.join(","));
        } else if (!Array.isArray(value)) {
          filteredParams = filteredParams.set(`${key}`, value);
        }
      }
    }

    return this.http.post<Pagination<NearbyHistoryCarWorkshopDTO[]>>(
      `${environment.apiUrl}${this.carWorkshopUrl}filter-map-paginated`,
      body,
      { params: filteredParams, headers: this.headers }
    );
  }

  getCarWorkshopFeeModelHistory(
    car_workshop_id: number,
    pageManager: PageManager,
    sort = ""
  ): Observable<CarWorkshopFeeHistoryResponse> {
    return this.http.get<CarWorkshopFeeHistoryResponse>(
      `${environment.apiUrl}carworkshop-fee-history/${car_workshop_id}?size=${pageManager.pageSize}&page=${pageManager.pageIndex}${sort}`
    );
  }

  getCarWorkshopByTaxIdentifier(
    taxIdentifier: string
  ): Observable<{ id: number; name: string }> {
    return this.http.get<{ id: number; name: string }>(
      `${environment.apiUrl}${this.carWorkshopUrl}tax-identifier`,
      { params: { taxIdentifier } }
    );
  }

  findCarWorkshopOriginById(
    id: number
  ): Observable<CarWorkshopOriginResponseDTO> {
    return this.http.get<CarWorkshopOriginResponseDTO>(
      environment.apiUrl + `${this.carWorkshopOriginUrl}${id}`
    );
  }

  editByCarWorkshop(
    id: number,
    carWorkshopOriginDTO: CarWorkshopOriginDTO
  ): Observable<CarWorkshopOriginDTO> {
    return this.http.put<CarWorkshopOriginDTO>(
      environment.apiUrl + `${this.carWorkshopOriginUrl}${id}`,
      carWorkshopOriginDTO
    );
  }

  findCarWorkshopOriginByFilters(
    carWorkshopLoggedId: number,
    searchFormDTO: SearchFormDTO,
    pageManager: PageManager,
    sort: string
  ): Observable<Pagination<CarWorkshopOriginResponseDTO[]>> {
    const query = this.buildQueryForFilters(searchFormDTO);

    return this.http.get<Pagination<CarWorkshopOriginResponseDTO[]>>(
      environment.apiUrl +
        `${this.carWorkshopOriginUrl}car-workshop/${carWorkshopLoggedId}?${query}&page=${pageManager.pageIndex}&size=${pageManager.pageSize}&${sort}`
    );
  }
}
